// /orders and /orders/:id: the order tracker. A signed quote becomes an order: Omni
// emails Phoneware about it (plan Phase 5), opens its tickets in Autotask by partner
// (Phase 6: Phoneware's own sales, a Dealer or a Reseller), then it waits for Billing ops
// or a Super admin to approve billing (#1621), then an account in OneBill, then the company becomes
// a Customer in Autotask. An order holds, with a sentence saying why, when a step fails,
// the customer's OneBill account is not reviewed or its Autotask type is one Omni won't
// change. Drawn as the prototype's chain of chips, with the numbers the systems report back.
import React, { useEffect, useState } from "react";
import { formatCents, quoteTotals, type CustomerV2, type Dealer, type Order, type Quote, type StepState } from "@phoneware/lens-domain";
import { companyOf, noticeOf } from "../api-customers.js";
import { Icon, type IconName } from "../icons.js";
import { SIDE_WORD, followsPartner, sideOf, ticketStage, type OrderSide } from "../order-tickets.js";
import { Link, rowLink } from "../router.js";
import { isNotYours, useLoad, useSession, viewerKind, type Session } from "../session.js";
import { ORDER_STATUS, STEP_STATE, atTime, errorText, plural, when, whenIn, type Look } from "../format.js";
import { Callout, Chip, Crumb, Head, NotYours, useTitle, useToast } from "../ui.js";
import { TypeTag } from "./customer-bits.js";
import { BillingApprovalSheet, DecisionChip, decisionOf, fromAutotask } from "./BillingApproval.js";
import { useRepNames } from "./Quotes.js";
import { DueNumbersCard, SetupCard, TicketsCard, ticketHold, ticketNote } from "./OrderTickets.js";
import { RecordActivity } from "./Activity.js";

/** The six steps every order runs, as a new order names them. */
function runSteps(o: Order, side: OrderSide | null): string[] {
  return ["Signed", "Phoneware told", ticketStage(o, side), "Billing approval", "Account in OneBill", "Customer in Autotask"];
}

/**
 * The stages as the order shows them. A new order shows its six steps. A MAC order shows the mock's four (plan 7C, M6):
 * Sold, which includes telling Phoneware; Source ticket; Billing approval; and In OneBill, which includes the company
 * step, since the customer is already a Customer in Autotask.
 */
export function stagesOf(o: Order, side: OrderSide | null = null): string[] {
  return o.kind === "mac" ? ["Sold", "Source ticket", "Billing approval", "In OneBill"] : runSteps(o, side);
}
/** Where billing approval sits in the stages: fourth of a new order's six, third of a MAC's four. */
const approvalAt = (o: Order) => (o.kind === "mac" ? 2 : 3);

/** Two steps shown as one: failed if either failed, otherwise the first until it's done, then the second. */
function together(first: StepState, second: StepState): StepState {
  if (first === "failed" || second === "failed") return "failed";
  return first === "done" ? second : first;
}

/** Each stage's state, in the order stagesOf names them. */
export function stageStates(o: Order): StepState[] {
  const rawNoticeState = noticeOf(o).state;
  const noticeState: StepState = rawNoticeState === "sending" ? "running" : rawNoticeState;
  const six: StepState[] = ["done", noticeState, o.autotask.state, approvalState(o), o.onebill.state, companyOf(o).state];
  return o.kind === "mac" ? [six[1], six[2], six[3], together(six[4], six[5])] : six;
}

type Drawn = "done" | "now" | "wait" | "fail" | "next";

/**
 * How someone reads an order that waits on Phoneware: "decides" approves billing and retries (Billing ops, Super admin),
 * "watches" is another Phoneware tier that sees the same queue without the buttons, "dealer" is the dealer's side.
 */
export type OrderViewer = "decides" | "watches" | "dealer";

export function orderViewer(session: Pick<Session, "can" | "me">): OrderViewer {
  const kind = viewerKind(session.me.user);
  switch (kind) {
    case "admin":
      return session.can("billing.approve") ? "decides" : "watches";
    case "dealer":
      return "dealer";
    default: {
      const unhandled: never = kind;
      throw new Error(`Unknown viewer ${String(unhandled)}`);
    }
  }
}

/** Billing approval as a step: done once Phoneware approved it after the ticket. Orders from before it existed read as approved. */
function approvalState(o: Order): StepState {
  const approved = !o.billingApproval || o.billingApproval.state === "approved";
  return approved && o.autotask.state === "done" ? "done" : "pending";
}

/**
 * Each stage as drawn: the first unfinished stage of a running order is the one in motion. An order waiting on
 * billing approval is Phoneware's move, so Phoneware sees it waiting and a dealer sees it in motion.
 */
export function drawnStages(o: Order, phoneware = false): Drawn[] {
  const states = stageStates(o);
  const current = states.findIndex((s) => s !== "done");
  return states.map((s, i) => {
    if (s === "done") return "done";
    if (s === "failed") return "fail";
    if (i === approvalAt(o) && o.status === "awaiting_billing_approval") return phoneware ? "wait" : "now";
    return s === "running" || (i === current && o.status === "in_progress") ? "now" : "next";
  });
}

/** Where the order is, in a few words. */
export function stageLabel(o: Order, side: OrderSide | null = null): string {
  if (o.status === "complete") return o.kind === "mac" ? "All four steps done" : "All six steps done";
  const stages = stagesOf(o, side);
  const at = drawnStages(o).findIndex((d) => d !== "done");
  const stage = at < 0 ? stages[stages.length - 1] : stages[at];
  return o.status === "needs_attention" ? `Held at ${stage}` : stage;
}

/** Whose sale an order is, as a chip: Phoneware's own sales stands out, the rest are quiet. */
export function SideChip({ side }: { side: OrderSide | null }): React.ReactElement | null {
  if (!side) return null;
  return (
    <Chip kind={side === "house" ? "house" : side === "mac" ? "ink" : "quiet"} icon={side === "house" ? "store" : null} testId="order-side" status={side}>
      {SIDE_WORD[side]}
    </Chip>
  );
}

export function Bar({ order, phoneware = false }: { order: Order; phoneware?: boolean }): React.ReactElement {
  const drawn = drawnStages(order, phoneware);
  const done = drawn.filter((d) => d === "done").length;
  return (
    <span className={drawn.length === 4 ? "bar5 bar4" : "bar5 bar6"} role="img" aria-label={`Step ${Math.min(done + 1, drawn.length)} of ${drawn.length}`}>
      {drawn.map((d, i) => (
        <i key={i} className={d === "next" ? undefined : d} />
      ))}
    </span>
  );
}

/** Who has the next move, as a chip. */
export function NextMove({ order, viewer }: { order: Order; viewer: OrderViewer }): React.ReactElement {
  if (order.status === "awaiting_billing_approval") {
    return viewer === "decides" ? (
      <Chip kind="warn" icon="triangle-alert">
        Approve billing
      </Chip>
    ) : viewer === "watches" ? (
      <Chip kind="quiet" icon="clock">
        Waiting on Billing ops
      </Chip>
    ) : (
      <Chip icon="clock">Waiting on Phoneware</Chip>
    );
  }
  if (order.status === "complete") {
    return (
      <Chip kind="ok" icon="check">
        Complete
      </Chip>
    );
  }
  if (order.status === "needs_attention") {
    return (
      <Chip kind="warn" icon="triangle-alert">
        {viewer === "decides" ? "Needs a retry" : viewer === "watches" ? "With Billing ops" : "With Phoneware"}
      </Chip>
    );
  }
  return (
    <Chip kind="quiet" icon="clock">
      Omni working
    </Chip>
  );
}

export function Orders(): React.ReactElement {
  const session = useSession();
  const { api, can, everyDealer } = session;
  const viewer = orderViewer(session);
  useTitle("Orders");
  const orders = useLoad<Order[]>(() => api.orders(), []);
  const dealers = useLoad<Dealer[]>(() => (everyDealer ? api.dealers() : Promise.resolve([])), [everyDealer]);
  const list = [...(orders.data ?? [])].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));

  return (
    <>
      <Head
        title="Orders"
        sub={
          everyDealer
            ? "Every signed quote becomes an order. Its tickets open in Autotask by partner: Phoneware\u2019s own sales, a Dealer or a Reseller. Then Phoneware approves billing and OneBill opens the account."
            : "Every quote your customers signed, on its way to live."
        }
        actions={
          // Plan 7C: Phoneware starts a MAC order from a customer's support ticket. Dealers never see it.
          can("mac.start") ? (
            <Link className="btn primary" to="/orders/mac/new" data-testid="orders-new-mac">
              <Icon name="plus" size={15} />
              New MAC order
            </Link>
          ) : null
        }
      />
      <section className="card">
        {orders.error ? (
          <Callout kind="stop" icon="triangle-alert">
            {orders.error.message}
          </Callout>
        ) : !orders.data ? (
          <div className="empty">Loading orders.</div>
        ) : list.length === 0 ? (
          <div className="empty">No orders yet. A quote becomes an order the moment the customer signs its QuoteLink.</div>
        ) : (
          <div className="tablewrap">
            <table className="t t-orders">
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Customer</th>
                  {everyDealer ? <th className="hide-sm">Dealer</th> : null}
                  <th className="hide-sm">Stage</th>
                  <th className="hide-sm">
                    <span className="sr">Progress</span>
                  </th>
                  <th>Next move</th>
                  <th className="hide-sm">Updated</th>
                </tr>
              </thead>
              <tbody>
                {list.map((o) => {
                  const dealer = dealers.data?.find((d) => d.id === o.dealerId) ?? null;
                  const side = everyDealer ? sideOf(o, dealer) : null;
                  // Plan 7C: a MAC order is tagged MAC for everyone and names the support ticket it came from.
                  const tag = side ?? (o.kind === "mac" ? "mac" : null);
                  const source = o.kind === "mac" ? (o.source?.ticketNumber ?? o.tickets?.find((t) => t.kind === "source")?.ticketNumber ?? null) : null;
                  return (
                    <tr key={o.id} {...rowLink(`/orders/${encodeURIComponent(o.id)}`)}>
                      <td className="mono">
                        <Link to={`/orders/${encodeURIComponent(o.id)}`}>{o.number}</Link>
                      </td>
                      <td>
                        <b>{o.customerName}</b>
                        <span className="sub">{source ? `From ticket ${source}` : `From ${o.quoteNumber}`}</span>
                        {tag ? (
                          <span className="sub ptn">
                            <SideChip side={tag} />
                          </span>
                        ) : null}
                      </td>
                      {everyDealer ? <td className="hide-sm">{dealer?.name ?? ""}</td> : null}
                      <td className="hide-sm">{stageLabel(o, side)}</td>
                      <td className="hide-sm">
                        <Bar order={o} phoneware={everyDealer} />
                      </td>
                      <td>
                        <NextMove order={o} viewer={viewer} />
                      </td>
                      <td className="muted hide-sm">{when(o.updatedAt)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}

const DRAWN_ICON: Record<Drawn, IconName | null> = { done: "check", now: "clock", wait: "triangle-alert", fail: "triangle-alert", next: null };

/** The email that tells Phoneware about a signed or sold order (plan Phase 5): Phoneware reads where it went, a dealer that nothing is theirs to send. */
export function noticeNote(o: Order, ops: boolean): string {
  if (o.notice === undefined) return "Created before Omni emailed Phoneware about signed orders.";
  const n = noticeOf(o);
  const to = n.to ?? "Phoneware";
  if (n.state === "done") {
    const at = n.sentAt ? ` ${atTime(n.sentAt)}` : "";
    return ops ? `Phoneware told${at}. An email to ${to} with the ${o.kind === "mac" ? "MAC summary" : "signed"} PDF.` : `Phoneware told${at}. Nothing for you to send.`;
  }
  if (n.state === "failed") return ops ? `Failed: ${n.error ?? "the email did not go out."}` : "Omni is telling Phoneware about this order.";
  if (n.state === "running") return `Emailing ${to}.`;
  return "Starts when Omni picks up the order.";
}

/** `companyType` is Autotask's type for the customer now, when it has been read. */
function stepNote(kind: "autotask" | "onebill" | "company", o: Order, companyType: number | null = null): string {
  if (kind === "autotask") {
    const s = o.autotask;
    if (s.state === "done") {
      const before = followsPartner(o) || o.kind === "mac" ? "" : " Opened before tickets followed the partner.";
      return `Ticket ${s.ticketNumber ?? ""} with ${plural(s.chargeIds.length, "charge")}, the signed PDF attached.${before}`;
    }
    if (s.state === "failed") return `Failed: ${s.error ?? "Autotask did not answer."}`;
    if (s.state === "running") return "Creating the ticket and its charges.";
    if (noticeOf(o).state !== "done") return "Starts once Phoneware is told.";
    return "Starts when Omni picks up the order.";
  }
  if (kind === "onebill") {
    const s = o.onebill;
    if (s.state === "done") return `Account ${s.accountNumber ?? ""} with ${plural(s.subscriptionIds.length, "subscription")}.`;
    // OneBill holds and refusals are whole sentences: say them as they are.
    if (s.state === "failed") return s.error ?? "OneBill did not answer.";
    if (s.state === "running") return "Creating the account, its subscriptions and charges.";
    if (approvalState(o) !== "done") return "Starts once billing is approved.";
    return "Starts next.";
  }
  const s = companyOf(o);
  if (s.state === "done") return s.fromType === 3 ? "The Prospect became a Customer, read back from Autotask." : s.fromType === 1 ? "Already a Customer in Autotask." : "Nothing to change in Autotask.";
  if (s.state === "failed") return s.error ?? "Autotask did not answer.";
  if (s.state === "running") return "Changing the company to a Customer in Autotask, then reading it back.";
  if (companyType === 1) return "Already a Customer in Autotask. Checked once OneBill has the lines.";
  if (companyType === 3) return "The Prospect becomes a Customer once OneBill has the lines, then Omni reads it back.";
  return "Waits for OneBill. A Prospect becomes a Customer, then Omni reads it back.";
}

/** `billingHold`: the OneBill step is held because the customer's OneBill account is not reviewed. */
function nextText(o: Order, viewer: OrderViewer, billingHold: boolean, prospect: boolean, side: OrderSide | null): string {
  const ops = viewer === "decides";
  if (o.status === "awaiting_billing_approval") {
    if (viewer === "watches") {
      return `Waiting on Billing ops since ${whenIn(o.billingApproval?.waitingSince ?? o.updatedAt)}. Billing ops or a Super admin approves billing. Omni makes no OneBill call until someone does.`;
    }
    // A dealer's order that follows its partner carries the due date and numbers Phoneware's ticket needs.
    if (!ops && followsPartner(o)) {
      return o.dueDate || o.numbersNote?.trim()
        ? `Nothing needed from you. Phoneware checks billing before OneBill bills ${o.customerName}, and has your due date and numbers on its ticket.`
        : `Add the due date and the numbers below so Phoneware\u2019s ticket has them. Phoneware checks billing before OneBill bills ${o.customerName}.`;
    }
    return ops
      ? "Approve billing. Choose the OneBill account, check the lines, then approve. Until then Omni makes no OneBill calls and leaves the Autotask company as it is."
      : `Nothing needed from you. Phoneware checks the OneBill account and the lines before anything is billed. Then OneBill sets up billing${prospect ? ` and ${o.customerName} becomes a Customer` : ""}.`;
  }
  if (o.status === "complete") return `Done. ${o.customerName} is a Customer in Autotask and bills in OneBill.`;
  if (o.status === "needs_attention") {
    if (viewer === "watches") {
      const tickets = ticketHold(o, side);
      return `${tickets ? `${tickets.title} ` : ""}Billing ops or a Super admin retries it here once it is fixed. A retry never makes a second ticket or account.`;
    }
    if (noticeOf(o).state === "failed") {
      const to = noticeOf(o).to ?? "Phoneware";
      return ops
        ? `The email to ${to} failed, so Omni stopped before the ticket. Retry once the mail service answers. A retry sends one email, then opens the ticket.`
        : "Nothing needed from you. Omni is telling Phoneware about this order, then opens the ticket in Autotask.";
    }
    const tickets = ticketHold(o, side);
    if (tickets && ops) return `${tickets.title} ${tickets.retry}`;
    if (billingHold) {
      return ops
        ? `Link ${o.customerName}'s OneBill account, or confirm it has none, then retry. Omni made no OneBill calls for this order.`
        : "Phoneware is linking this customer's OneBill account. Nothing is billed until then.";
    }
    if (companyOf(o).state === "failed") {
      return ops
        ? "Fix the company type in Autotask, then retry. The ticket and OneBill account stay as they are."
        : "Phoneware is fixing the customer's record in Autotask. Billing is already set up.";
    }
    return ops
      ? "A step failed. Fix what it says, then retry. A retry picks up where the order stopped and never makes a second ticket or account."
      : "A step needs Phoneware's attention. Phoneware can see it here and retry it. Nothing is needed from you.";
  }
  if (o.onebill.state === "done") return "Nothing needed from you. Omni is making the company a Customer in Autotask.";
  return o.autotask.state === "done"
    ? "Nothing needed from you. Omni is setting up billing in OneBill."
    : "Nothing needed from you. Omni is opening the ticket in Autotask, then setting up billing in OneBill.";
}

export function OrderScreen({ id }: { id: string }): React.ReactElement {
  const session = useSession();
  const { me, api, can, everyDealer } = session;
  const toast = useToast();
  // Phoneware reads the order's insides (who did what, the tickets, the holds); Billing ops and Super admins act on them.
  const ops = everyDealer;
  const viewer = orderViewer(session);
  const approves = can("billing.approve");
  const runs = can("orders.run");
  const loaded = useLoad<Order>(() => api.order(id), [id]);
  const order = loaded.data;
  const quote = useLoad<Quote | null>(() => (order ? api.quote(order.quoteId) : Promise.resolve(null)), [order?.quoteId]);
  // Ops read every dealer; a dealer's people read their own. Either way the order's dealer says whose sale it is.
  const dealers = useLoad<Dealer[]>(() => api.dealers(), []);
  // The customer as Autotask has it now: its type and whether its OneBill account is reviewed.
  const customer = useLoad<CustomerV2 | null>(() => (order ? api.customer(order.customerId) : Promise.resolve(null)), [order?.customerId, order?.status]);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [approving, setApproving] = useState(false);
  const repName = useRepNames();
  useTitle(order ? order.number : "Order");

  // Lens runs the steps on its own; keep reading until the order settles.
  const running = order?.status === "in_progress";
  useEffect(() => {
    if (!running) return;
    const timer = window.setInterval(() => loaded.reload(), 3000);
    return () => window.clearInterval(timer);
  }, [running]);

  if (!order) {
    if (isNotYours(loaded.error)) return <NotYours kind="order" back="/orders" />;
    return (
      <>
        <Crumb to="/orders">Orders</Crumb>
        {loaded.error ? (
          <Callout kind="stop" icon="triangle-alert">
            {loaded.error.message}
          </Callout>
        ) : (
          <div className="empty">Loading the order.</div>
        )}
      </>
    );
  }

  async function retry(): Promise<void> {
    setBusy(true);
    setProblem(null);
    try {
      loaded.set(await api.retryOrder(id));
      toast("Retrying the failed step.");
    } catch (err) {
      setProblem(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  const drawn = drawnStages(order, ops);
  const dealer = dealers.data?.find((d) => d.id === order.dealerId) ?? null;
  const side = sideOf(order, dealer);
  const stages = stagesOf(order, side);
  const run = runSteps(order, side);
  const company = companyOf(order);
  const notice = noticeOf(order);
  const noticeFailed = notice.state === "failed";
  const awaiting = order.status === "awaiting_billing_approval";
  // Waiting on billing approval is Billing ops' move; another tier sees Billing ops has it, a dealer that Phoneware has it.
  const waitingLook: Look = viewer === "watches" ? { label: "Waiting on Billing ops", kind: "quiet", icon: "clock" } : { label: "Waiting on Phoneware", kind: "", icon: "clock" };
  const look: Look = awaiting ? (approves ? { label: "Approve billing", kind: "warn", icon: "triangle-alert" } : waitingLook) : ORDER_STATUS[order.status];
  const approval = order.billingApproval;
  const approvedOn =
    approval?.account?.kind === "existing" ? approval.account.accountNumber : approval?.account?.kind === "create" ? "a new OneBill account" : "the linked account";
  const approvalNote = awaiting
    ? approves
      ? `Waiting on you since ${whenIn(approval?.waitingSince ?? order.updatedAt)}. Omni makes no OneBill call until someone approves.`
      : ops
      ? `Waiting on Billing ops since ${whenIn(approval?.waitingSince ?? order.updatedAt)}. Billing ops or a Super admin approves billing. Omni makes no OneBill call until someone does.`
      : "Waiting on Phoneware. Phoneware checks billing before OneBill bills this order."
    : approvalState(order) !== "done"
      ? run[2] === "Tickets in Autotask"
        ? "Starts once the tickets are in."
        : "Starts once the ticket is in Autotask."
      : !approval?.decidedBy
        ? "Created before billing approval existed."
        : `${ops ? repName(approval.decidedBy) : "Phoneware"} approved billing on ${approvedOn}.${approval.note ? ` "${approval.note}"` : ""}`;
  const q = quote.data;
  const totals = q ? quoteTotals(q.lines, dealer?.model ?? me.dealer?.model ?? "agent") : null;
  const six: Array<{ label: string; note: string; when: string | null; attempts: number; chip?: Look }> = [
    {
      label: order.kind === "mac" ? "Sold" : run[0],
      note:
        order.kind === "mac"
          ? `${order.soldBy ? (ops ? repName(order.soldBy) : "Phoneware") : "Phoneware"} marked it sold. No customer signature.`
          : q?.signature
            ? `${q.signature.name} signed the QuoteLink.`
            : "Signed on the QuoteLink.",
      when: order.kind === "mac" ? (order.soldAt ?? order.createdAt) : (q?.signature?.signedAt ?? order.createdAt),
      attempts: 0,
    },
    { label: run[1], note: noticeNote(order, ops), when: null, attempts: 0 },
    {
      label: run[2],
      note: ticketNote(order, side, ops) ?? stepNote("autotask", order),
      when: order.autotask.state === "pending" ? null : order.autotask.updatedAt,
      attempts: order.autotask.attempts,
    },
    {
      label: run[3],
      note: approvalNote,
      when: awaiting ? null : (approval?.decidedAt ?? null),
      attempts: 0,
      chip: awaiting ? (approves ? { label: "Your move", kind: "warn", icon: "triangle-alert" } : waitingLook) : undefined,
    },
    { label: run[4], note: stepNote("onebill", order), when: order.onebill.state === "pending" ? null : order.onebill.updatedAt, attempts: order.onebill.attempts },
    { label: run[5], note: stepNote("company", order, customer.data?.companyType ?? null), when: company.state === "pending" ? null : company.updatedAt, attempts: company.attempts },
  ];
  // A MAC order shows four (plan 7C): telling Phoneware reads under Sold, and the company step under In OneBill.
  const steps =
    order.kind === "mac"
      ? [
          { ...six[0], note: `${six[0].note} ${six[1].note}` },
          { ...six[2], label: stages[1] },
          six[3],
          { ...six[4], label: stages[3], note: `${six[4].note} ${six[5].note}`, when: six[5].when ?? six[4].when },
        ]
      : six;
  const states = stageStates(order);
  const prospect = customer.data ? customer.data.companyType === 3 : company.fromType === 3;
  const linkedAccount = customer.data?.billing.state === "linked" ? customer.data.billing.accountNumber : null;
  const decision = awaiting && approves && customer.data && q ? decisionOf(customer.data.billing, fromAutotask(customer.data, q).missing) : null;
  const billingHold = order.onebill.state === "failed" && customer.data?.billing.state === "unreviewed";
  const heldAt = order.status === "needs_attention" ? stages[drawn.findIndex((d) => d === "fail")] ?? null : null;
  // An order held at its tickets (plan 6B): Phoneware reads which ticket wasn't made; a dealer reads that Phoneware is on it.
  const ticketsHeld = order.status === "needs_attention" && !noticeFailed ? ticketHold(order, side) : null;
  const heldError = noticeFailed ? notice.error : order.onebill.state === "failed" ? order.onebill.error : company.state === "failed" ? company.error : order.autotask.error;

  return (
    <>
      <Crumb to="/orders">Orders</Crumb>
      <Head
        title={
          <>
            {order.customerName}
            <Chip kind={look.kind} icon={look.icon} testId="order-status" status={order.status}>
              {look.label}
            </Chip>
          </>
        }
        sub={
          <>
            <span data-testid="order-number">{order.number}</span> · from quote <Link to={`/quotes/${encodeURIComponent(order.quoteId)}`}>{order.quoteNumber}</Link>
            {/* Plan 7C: a MAC order names the support ticket it came from. */}
            {order.kind === "mac" && order.source ? <span data-testid="order-source">{` \u00b7 from ticket ${order.source.ticketNumber}`}</span> : null}
            {ops && dealer ? ` \u00b7 ${dealer.name}` : ""}
            {ops ? (
              <>
                {" "}
                <SideChip side={side} />
              </>
            ) : null}
          </>
        }
        actions={
          runs && order.status === "needs_attention" ? (
            <>
              {billingHold ? (
                <Link className="btn" to={`/customers/${order.customerId}?billing=review`} data-testid="order-review-billing">
                  Review OneBill account
                </Link>
              ) : null}
              <button className="btn primary" type="button" disabled={busy} onClick={() => void retry()}>
                <Icon name="refresh-cw" size={15} />
                {busy ? "Retrying" : "Retry the failed step"}
              </button>
            </>
          ) : approves && awaiting ? (
            <button className="btn primary" type="button" data-testid="order-approve-billing" onClick={() => setApproving(true)}>
              <Icon name="receipt" size={15} />
              Approve billing
            </button>
          ) : null
        }
      />
      {problem ? (
        <Callout kind="stop" icon="triangle-alert">
          {problem}
        </Callout>
      ) : null}
      {heldAt && noticeFailed && ops ? (
        <div className="callout stop hold" role="alert" data-testid="order-hold" data-step="notice">
          <Icon name="triangle-alert" size={15} />
          <div>
            <b>Phoneware wasn{"\u2019"}t told.</b>
            <span className="sub-l">
              {heldError ?? "The email did not go out."} Omni tried {plural(notice.attempts, "time")}.
            </span>
          </div>
        </div>
      ) : ticketsHeld ? (
        ops ? (
          <div className="callout stop hold" role="alert" data-testid="order-hold" data-step="tickets">
            <Icon name="triangle-alert" size={15} />
            <div>
              <b>{ticketsHeld.title}</b>
              <span className="sub-l">{ticketsHeld.detail}</span>
            </div>
          </div>
        ) : null
      ) : heldAt && !noticeFailed ? (
        <div className="callout stop hold" role="alert" data-testid="order-hold">
          <Icon name="triangle-alert" size={15} />
          <p>
            <b>On hold at {heldAt}.</b> {heldError ?? "A step failed."}
          </p>
        </div>
      ) : null}
      <section className="card">
        <div className="card-h">
          <h2>Where it is</h2>
          <span className="note">
            {awaiting
              ? approves
                ? `Waiting on you since ${whenIn(approval?.waitingSince ?? order.updatedAt)}.`
                : ops
                  ? `Waiting on Billing ops since ${whenIn(approval?.waitingSince ?? order.updatedAt)}.`
                  : "Waiting on Phoneware."
              : order.status === "in_progress"
                ? "Omni is working on it."
                : order.status === "complete"
                  ? "Done."
                  : ops
                    ? heldAt
                      ? `Held at ${heldAt}.`
                      : "With Billing ops."
                    : "With Phoneware."}
          </span>
        </div>
        <div className="chain">
          {stages.map((label, i) => {
            const icon = DRAWN_ICON[drawn[i]];
            return (
              <React.Fragment key={label}>
                {i ? (
                  <span className="sep">
                    <Icon name="chevron-right" size={14} />
                  </span>
                ) : null}
                <span className={`step ${drawn[i]}`}>
                  {icon ? <Icon name={icon} size={13} /> : null}
                  {label}
                </span>
              </React.Fragment>
            );
          })}
        </div>
      </section>
      <div className="grid2 start">
        <div className="stack">
          <section className="card">
            <div className="card-h">
              <h2>Step by step</h2>
              <span className="note">As Autotask and OneBill answer</span>
            </div>
            <ol className="timeline">
              {steps.map((s, i) => {
                const look = s.chip ?? STEP_STATE[states[i]];
                return (
                  <li key={s.label} className={`tl tl-${drawn[i]}`}>
                    <span className="tl-dot" aria-hidden="true">
                      {drawn[i] === "done" ? <Icon name="check" size={12} /> : null}
                    </span>
                    <div className="tl-main">
                      <b>{s.label}</b>
                      <span className="row-m">{s.note}</span>
                      {s.when ? (
                        <span className="row-m">
                          {when(s.when)}
                          {s.attempts > 1 ? ` \u00b7 ${plural(s.attempts, "try", "tries")}` : ""}
                        </span>
                      ) : null}
                    </div>
                    <Chip kind={look.kind} icon={look.icon}>
                      {s.chip
                        ? look.label
                        : states[i] === "failed" && order.status === "needs_attention"
                          ? "Held"
                          : (drawn[i] === "now" || drawn[i - 1] === "fail") && states[i] === "pending"
                            ? "Up next"
                            : look.label}
                    </Chip>
                  </li>
                );
              })}
            </ol>
          </section>
          {ops ? <TicketsCard order={order} side={side} busy={busy} onRetry={runs ? () => void retry() : null} /> : <SetupCard order={order} side={side} />}
        </div>
        <div className="stack">
          <section className={(order.status === "needs_attention" && viewer !== "watches") || (approves && awaiting) ? "card todo-on" : "card"}>
            <div className="card-h">
              <h2>What happens next</h2>
              {approves && awaiting ? (
                <Chip kind="warn" icon="triangle-alert">
                  Your move
                </Chip>
              ) : null}
            </div>
            <p className="todo-t">{nextText(order, viewer, billingHold, prospect, side)}</p>
            {decision ? (
              <div className="todo-do" data-testid="order-decision">
                <DecisionChip decision={decision} /> <span className="muted small">{decision.text}</span>
              </div>
            ) : null}
          </section>
          <DueNumbersCard order={order} side={side} onChanged={(next) => loaded.set(next)} />
          <section className="card">
            <div className="card-h">
              <h2>In Autotask and OneBill</h2>
              <span className="note">As Autotask and OneBill report them</span>
            </div>
            <dl className="facts">
              <div>
                <dt>Autotask ticket</dt>
                <dd>{order.autotask.ticketNumber ? <span data-testid="order-autotask-ticket">{order.autotask.ticketNumber}</span> : <span className="muted">Not yet</span>}</dd>
              </div>
              <div>
                <dt>OneBill account</dt>
                <dd>
                  {order.onebill.accountNumber ? (
                    <span data-testid="order-onebill-account">{order.onebill.accountNumber}</span>
                  ) : awaiting && linkedAccount ? (
                    <span data-testid="order-onebill-account">{linkedAccount} (linked)</span>
                  ) : (
                    <span className="muted">Not yet</span>
                  )}
                </dd>
              </div>
              <div>
                <dt>Subscriptions</dt>
                <dd>{order.onebill.placed.length ? `${order.onebill.subscriptionIds.length} of ${order.onebill.placed.length}` : <span className="muted">None placed</span>}</dd>
              </div>
              <div>
                <dt>Company type in Autotask</dt>
                <dd data-testid="order-company-type">
                  {customer.data ? <TypeTag type={customer.data.companyType} /> : company.fromType !== null ? <TypeTag type={company.fromType} /> : <span className="muted">{customer.error ? "Not readable" : "Reading"}</span>}
                </dd>
              </div>
            </dl>
          </section>
          <section className="card">
            <div className="card-h">
              <h2>The order</h2>
              {totals ? (
                <span className="note">
                  {formatCents(totals.monthly)} a month, {formatCents(totals.oneTime)} one-time
                </span>
              ) : null}
            </div>
            {q ? (
              <div className="tablewrap">
                <table className="t">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th className="num">Qty</th>
                      <th className="num">Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {q.lines.map((l) => (
                      <tr key={l.productId}>
                        <td>
                          {l.name} {l.code ? <span className="code">{l.code}</span> : null}
                        </td>
                        <td className="num">{l.qty}</td>
                        <td className="num">
                          {formatCents(l.unitPrice)}
                          <span className="sub">{l.billing === "monthly" ? "a month" : "once"}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="empty">{quote.error ? quote.error.message : "Loading the lines."}</div>
            )}
            <div className="refs">
              <Link to={`/customers/${order.customerId}`}>
                <Icon name="building-2" size={14} />
                Customer
              </Link>
              <Link to={`/quotes/${encodeURIComponent(order.quoteId)}`}>
                <Icon name="file-text" size={14} />
                {order.kind === "mac" ? "MAC quote" : "Signed quote"}
              </Link>
            </div>
          </section>
          <RecordActivity kind="order" id={order.id} version={order.updatedAt} />
        </div>
      </div>
      {approving ? (
        <BillingApprovalSheet
          orderId={order.id}
          onChanged={(next) => loaded.set(next)}
          onClose={() => {
            setApproving(false);
            loaded.reload();
          }}
        />
      ) : null}
    </>
  );
}
